import React, { useState, useRef, useEffect } from 'react';
import Header from './components/Header';
import Typography from '@mui/material/Typography';
import PageController from './components/PageControl';
import MainColorWheel from './pages/MainColorWheel';
import Animations from './pages/Animations/Animations';
import Draw from './pages/Draw';
import Settings from './pages/Settings';
import Footer from './components/Footer';
import { Box } from '@mui/material';
import { sendBluetoothData } from './utils/BluetoothUtils';

const LEDController = () => {
  const [profiles, setProfiles] = useState(() => {
    const savedProfiles = localStorage.getItem('ledProfiles');
    return savedProfiles
      ? JSON.parse(savedProfiles)
      : [
          {
            name: 'Default Profile 1',
            channels: Array.from({ length: 6 }, (_, i) => ({
              name: i === 5 ? 'ALL LEDS' : `Channel ${i}`,
              ledCount: i === 5 ? 300 : 150,
              width: 300,
              height: 1,
            })),
          },
        ];
  });

  const [deviceInfo, setDeviceInfo] = useState(() => {
    const saved = localStorage.getItem('deviceInfo');
    return saved ? JSON.parse(saved) : null;
  });
  const [arduinoVersion, setArduinoVersion] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedProfileIndex, setSelectedProfileIndex] = useState(0);
  const [currentChannelIndex, setCurrentChannelIndex] = useState(0);
  const [activePage, setActivePage] = useState('mainColorWheel');
  const [ledColor, setLEDColor] = useState('rgb(255, 255, 255)');
  const [brightness, setBrightness] = useState(100);
  const [responses, setResponses] = useState([]);
  const lastSentTime = useRef(0);
  const [connectedDevice, setConnectedDevice] = useState(null);

  // Handle device disconnection (web only)
  useEffect(() => {
    if (
      connectedDevice &&
      !isMobile &&
      typeof connectedDevice.addEventListener === 'function'
    ) {
      const handleDisconnect = () => {
        console.log('Device disconnected');
        setConnectedDevice(null);
        setResponses((prev) => [...prev, 'Error: Device disconnected']);
      };
      connectedDevice.addEventListener(
        'gattserverdisconnected',
        handleDisconnect
      );
      return () => {
        if (typeof connectedDevice.removeEventListener === 'function') {
          connectedDevice.removeEventListener(
            'gattserverdisconnected',
            handleDisconnect
          );
        }
      };
    }
  }, [connectedDevice, isMobile]);

  // Listen for deviceInfo from iOS
  useEffect(() => {
    function handleDeviceInfo(event) {
      const info = event.detail;
      console.log('Received device info from iOS:', info);
      setDeviceInfo(info);
      if (info.platform === 'iOS' || info.platform === 'Android') {
        setIsMobile(true);
      }
      localStorage.setItem('deviceInfo', JSON.stringify(info));
    }

    window.addEventListener('deviceInfo', handleDeviceInfo);
    return () => window.removeEventListener('deviceInfo', handleDeviceInfo);
  }, []);

  const handleArduinoReceivedMessage = (value) => {
    if (value.startsWith('v')) {
      const version = value.trim();
      setArduinoVersion(version);
      const stored = JSON.parse(localStorage.getItem('recentDevices')) || [];
      const updated = stored.map((d) =>
        d.id === connectedDevice.id ? { ...d, version } : d
      );
      localStorage.setItem('recentDevices', JSON.stringify(updated));
    } else {
      // fallback: log raw
      setResponses((prev) => [...prev, `Received: ${value}`]);
    }
  };

  // --- ✅ Formatting helpers ---
  const formatColorMessage = (
    channelIndex,
    channel,
    red,
    green,
    blue,
    brightness
  ) => {
    const scaledRed = Math.round((red * brightness) / 100);
    const scaledGreen = Math.round((green * brightness) / 100);
    const scaledBlue = Math.round((blue * brightness) / 100);

    return (
      `${channelIndex}` +
      `${channel.width.toString().padStart(3, '0')}` +
      `${channel.height.toString().padStart(3, '0')}` +
      `00` + // animation=00 means solid color
      `${scaledRed.toString().padStart(3, '0')}` +
      `${scaledGreen.toString().padStart(3, '0')}` +
      `${scaledBlue.toString().padStart(3, '0')}`
    );
  };

  const formatAnimationMessage = (
    channelIndex,
    channel,
    animationCode,
    speed
  ) => {
    return (
      `${channelIndex}` +
      `${channel.width.toString().padStart(3, '0')}` +
      `${channel.height.toString().padStart(3, '0')}` +
      `${animationCode.toString().padStart(2, '0')}` +
      `${speed.toString().padStart(1, '0')}`
    );
  };

  // --- ✅ Send Color ---
  const handleSendColorToPhone = async (red, green, blue, brightness) => {
    const now = Date.now();
    if (now - lastSentTime.current < 500) return;
    lastSentTime.current = now;

    const currentProfile = profiles[selectedProfileIndex];
    const currentChannel = currentProfile?.channels[currentChannelIndex];
    if (!currentChannel) {
      setResponses((prev) => [...prev, 'Error: Invalid profile/channel']);
      return;
    }

    const msg = formatColorMessage(
      currentChannelIndex,
      currentChannel,
      red,
      green,
      blue,
      brightness
    );
    const { success, message } = await sendBluetoothData(
      connectedDevice,
      msg,
      isMobile,
      handleArduinoReceivedMessage
    );

    setResponses((prev) => [
      ...prev,
      success ? `Sent Color: ${message}` : `Error: ${message}`,
    ]);
  };

  // --- ✅ Send Animation ---
  const handleSendAnimationToPhone = async (animationCode, speed) => {
    const now = Date.now();
    if (now - lastSentTime.current < 500) return;
    lastSentTime.current = now;

    const currentProfile = profiles[selectedProfileIndex];
    const currentChannel = currentProfile?.channels[currentChannelIndex];
    if (!currentChannel) {
      setResponses((prev) => [...prev, 'Error: Invalid profile/channel']);
      return;
    }

    const msg = formatAnimationMessage(
      currentChannelIndex,
      currentChannel,
      animationCode,
      speed
    );
    const { success, message } = await sendBluetoothData(
      connectedDevice,
      msg,
      isMobile,
      handleArduinoReceivedMessage
    );

    setResponses((prev) => [
      ...prev,
      success ? `Sent Animation: ${message}` : `Error: ${message}`,
    ]);
  };

  const renderContent = () => {
    switch (activePage) {
      case 'mainColorWheel':
        return (
          <MainColorWheel
            initialLedColor={ledColor}
            initialBrightness={brightness}
            onSendColor={({ red, green, blue, brightness }) =>
              handleSendColorToPhone(red, green, blue, brightness)
            }
          />
        );
      case 'animations':
        return (
          <Animations handleSendAnimationToPhone={handleSendAnimationToPhone} />
        );
      case 'draw':
        const currentProfile = profiles[selectedProfileIndex];
        const currentChannel = currentProfile?.channels[currentChannelIndex];
        if (!currentChannel)
          return <Typography color="error">Invalid profile/channel</Typography>;
        return (
          <Draw
            settings={{
              width: currentChannel.width,
              height: currentChannel.height,
              ledCount: currentChannel.ledCount,
            }}
          />
        );
      case 'settings':
        return (
          <Settings
            profiles={profiles}
            selectedProfileIndex={selectedProfileIndex}
            onUpdateProfiles={(updatedProfiles, index) => {
              setProfiles(updatedProfiles);
              if (index !== undefined) setSelectedProfileIndex(index);
              localStorage.setItem(
                'ledProfiles',
                JSON.stringify(updatedProfiles)
              );
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ paddingBottom: '120px' }}>
      <Header
        profiles={profiles}
        currentProfileIndex={selectedProfileIndex}
        currentChannelIndex={currentChannelIndex}
        onProfileChange={setSelectedProfileIndex}
        onChannelChange={setCurrentChannelIndex}
        onConnect={() => console.log('Connected')}
        connectedDevice={connectedDevice}
        setConnectedDevice={setConnectedDevice}
        deviceInfo={deviceInfo}
        isMobile={isMobile}
        onPageChange={setActivePage}
        arduinoVersion={arduinoVersion}
      />
      <PageController activePage={activePage} onPageChange={setActivePage} />
      {renderContent()}
      <Footer responses={responses} />
    </Box>
  );
};

export default LEDController;
